import React, { useState, useEffect, useCallback, memo } from 'react';
import { Map } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getChanged } from '../../utils/ChangeSpy';
import * as TARGET from '../../actions/company/companyTarget';
import * as BUYER from '../../actions/company/buyer/marketing';
import { basicAccess } from '../../utils/checkPermissions';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

const EditEmailContactChannelPopupContainer = ({
  children,
  channel,
  isValid,
  isNew,
  contactId,
  channelIndex,
  channelId,
  pathPrefix,
  popup,
  inputErrors,
  user,
  verifyEmail,
  handleUpdateCompany,
  saveCompanyContactChannel,
  revertUpdate,
  closeValidationError,
  updateLastResearcher,
}) => {
  const context = usePopupContext();
  const [isBasicAccess, setIsBasicAccess] = useState(false);

  useEffect(() => {
    setIsBasicAccess(basicAccess(user.getIn(['roles', 0, 'slug'])));

    context.onClosePopup(() => {
      const field = pathPrefix.slice(0, -1);

      revertUpdate({ field });
    });
  }, []);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      return handleUpdateCompany({
        name: `${pathPrefix}${name}`,
        value,
      });
    },
    [handleUpdateCompany, pathPrefix],
  );

  const handleSave = useCallback(() => {
    const companyId = popup.getIn(['props', 'params', 'companyId']);
    const mode = popup.getIn(['props', 'mode']);
    const isVerify = verifyEmail.get('status') === 'valid';

    if (isValid) {
      const body = {
        ...getChanged(channel),
        ...(isVerify && { isVerify }),
      };

      saveCompanyContactChannel(
        {
          companyId,
          isNew,
          contactId,
          channelType: 'emails',
          channelId,
          channelIndex,
          body,
        },
        () => {
          context.closePopup();
          if (isBasicAccess && mode !== 'buyer') {
            updateLastResearcher(user.get('userName'));
          }
        },
      );
    }
  }, [
    channel,
    isValid,
    isNew,
    contactId,
    channelIndex,
    channelId,
    popup,
    verifyEmail,
    saveCompanyContactChannel,
    updateLastResearcher,
    user,
    isBasicAccess,
    context.closePopup,
  ]);

  const handleFindEmail = useCallback(() => {
    context.openPopup('EmailFinderPopup', {
      channel,
      isValid,
      isNew,
      contactId,
      channelIndex,
      channelId,
      pathPrefix,
      popup,
      inputErrors,
      user,
      verifyEmail,
      reopenPopupName: 'EditEmailContactChannelPopup',
    });
  }, [context.openPopup]);

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.EditEmailContactChannelPopup
          channel={channel}
          isNew={isNew}
          isValid={isValid}
          onChange={handleChange}
          onFindEmail={handleFindEmail}
          onSave={handleSave}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  const openedContact = props.popup.getIn(['props', 'openedContact'], 0);
  const channelType = 'emails';
  const channelIndex = props.popup.getIn(['props', 'channelIndex']);
  const channelId = props.popup.getIn(['props', 'channelId']);
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';
  const root = isBuyer ? state.targetCompany.buyer.marketing : state.targetCompany.target;
  const contact = root.getIn(['contacts', openedContact]);
  const contactId = contact.get('id');
  const isNew = !parseInt(channelId, 10);
  const pathPrefix = isNew ? `newData.${channelType}.` : `contacts.${openedContact}.${channelType}.${channelIndex}.`;
  const channel = root.getIn(pathPrefix.split('.').slice(0, -1));

  return {
    isValid: channel.get('isValid', true),
    isNew,
    inputErrors: channel.get('inputErrors', Map()),
    channel,
    channelId,
    channelIndex,
    contactId,
    pathPrefix,
    contact,
    verifyEmail: state.verifyEmail,
    user: state.auth.get('user', null),
  };
}

function mapDispatchToProps(dispatch, props) {
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';

  if (isBuyer) {
    const { handleUpdateCompany, saveCompanyContactChannel, revertUpdate, closeValidationError } = BUYER;

    return bindActionCreators(
      {
        handleUpdateCompany,
        saveCompanyContactChannel,
        revertUpdate,
        closeValidationError,
      },
      dispatch,
    );
  }

  const {
    handleUpdateCompany,
    saveCompanyContactChannel,
    revertUpdate,
    closeValidationError,
    updateLastResearcher,
  } = TARGET;

  return bindActionCreators(
    {
      handleUpdateCompany,
      saveCompanyContactChannel,
      revertUpdate,
      closeValidationError,
      updateLastResearcher,
    },
    dispatch,
  );
}

export { EditEmailContactChannelPopupContainer };

const Connected = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(EditEmailContactChannelPopupContainer));

export default Connected;
